import type { ControlTourStep } from "./control-tour.model";

const visible = (element: HTMLElement): boolean =>
  element.getClientRects().length > 0 &&
  !element.closest('[hidden], [aria-hidden="true"], [inert]') &&
  getComputedStyle(element).visibility !== "hidden";
const normalized = (text: string | null | undefined) => (text ?? "").replace(/\s+/gu, " ").trim().replace(/\s*\*$/u, "").toLowerCase();

export const findControlTourTarget = (
  rootSelector: string,
  step: Pick<ControlTourStep, "target" | "label">,
): HTMLElement | null => {
  const root = typeof document === "undefined" ? null : document.querySelector(rootSelector);
  const element = Array.from(root?.querySelectorAll<HTMLElement>(step.target) ?? []).find((candidate) =>
    visible(candidate) && (!step.label || [candidate.getAttribute("aria-label"), candidate.getAttribute("title"), candidate.textContent]
      .some((label) => normalized(label) === normalized(step.label))),
  );
  if (!element) return null;
  if (element.matches("label")) {
    const label = element as HTMLLabelElement;
    const input = label.control ?? (label.htmlFor ? document.getElementById(label.htmlFor) : null);
    return input?.closest<HTMLElement>(".MuiFormControl-root, .MuiFormControlLabel-root") ?? input ?? element;
  }
  return element;
};

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
const sameRect = (a: DOMRect, b: DOMRect) =>
  (["x", "y", "width", "height"] as const).every((key) => Math.abs(a[key] - b[key]) < 0.5);

export const watchControlTourTarget = (target: HTMLElement, reposition: () => void): (() => void) => {
  let rect = target.getBoundingClientRect();
  let frame: number | null = null;
  const check = () => {
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      if (!target.getClientRects().length) { reposition(); return; }
      const next = target.getBoundingClientRect();
      if (sameRect(rect, next)) return;
      rect = next;
      reposition();
    });
  };
  const root = target.closest("[data-route-path]") ?? target;
  const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(check);
  resize?.observe(target);
  if (root !== target) resize?.observe(root);
  const mutations = typeof MutationObserver === "undefined" ? null : new MutationObserver(check);
  mutations?.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class", "hidden"] });
  document.addEventListener("scroll", check, { capture: true, passive: true });
  window.addEventListener("resize", check);
  return () => {
    if (frame !== null) cancelAnimationFrame(frame);
    resize?.disconnect();
    mutations?.disconnect();
    document.removeEventListener("scroll", check, { capture: true });
    window.removeEventListener("resize", check);
  };
};

export const prepareControlTourTarget = async (root: string, step: Pick<ControlTourStep, "target" | "label" | "reveal">): Promise<void> => {
  if (step.reveal && !findControlTourTarget(root, step)) {
    const reveal = findControlTourTarget(root, { target: step.reveal });
    if (reveal?.matches('[role="tab"]:not([disabled]):not([aria-disabled="true"]), button[aria-expanded="false"]:not([disabled]), summary, [data-tour="analysis-history-summary"][data-tour-expanded="false"]')) {
      reveal.click();
      await nextFrame();
      await nextFrame();
    }
  }
  if (!findControlTourTarget(root, step) && typeof MutationObserver !== "undefined") {
    await new Promise<void>((resolve) => {
      const finish = () => { observer.disconnect(); clearTimeout(timer); resolve(); };
      const observer = new MutationObserver(() => { if (findControlTourTarget(root, step)) finish(); });
      const timer = setTimeout(finish, 1_200);
      const container = document.querySelector(root);
      if (!container) { finish(); return; }
      observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "style", "aria-hidden"] });
    });
  }
  findControlTourTarget(root, step)?.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
  let previous: DOMRect | undefined;
  let previousTarget: HTMLElement | null = null;
  let stableFrames = 0;
  // Measure the actual target, not an assumed scroll duration. The cap prevents
  // animated or removed content from leaving the tour permanently waiting.
  for (let frame = 0; frame < 120; frame++) {
    await nextFrame();
    const target = findControlTourTarget(root, step);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const stable = target === previousTarget && previous && sameRect(rect, previous);
    stableFrames = stable ? stableFrames + 1 : 0;
    if (stableFrames >= 6) return;
    previous = rect;
    previousTarget = target;
  }
};
